import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EquipmentClient } from '@frontend/equipment/api';
import { EquipmentStateName } from '@frontend/equipment/types';
import { Application, Feature } from '@frontend/functionality/types';
import { IProduct } from '@frontend/product/types';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedVendingTransaction, TransactionStateName } from '@frontend/transaction/types';
import { useMemo, useState } from 'react';

import { useLicenseRepository } from '../../../utils/license-repository';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading: boolean;
    setIsLoading: (isLoading: boolean) => void;
    fetchData: (queryParams?: ApiQueryParams<DefaultQueryParams>) => Promise<PaginatedResponse<StockExtended | ExtendedVendingTransaction>>;
    onClick: (product: IProduct, object: StockExtended | ExtendedVendingTransaction) => Promise<void>;
}

const useReviewReturns = (): ViewProps => {
    const { functionalities } = useLicenseRepository();
    const { updateWizardData, onSubmit, store, wizardId, navigationState } = useScreen();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { localUpdateTransaction } = useWizardTransaction({
        store,
        wizardId
    });

    const allowedTypes = useMemo(() => {
        return functionalities.filter((f) => f.feature === Feature.REVIEW && f.application !== Application.SHOP).map((f) => f.application);
    }, [functionalities]);

    const onClick = async (product: IProduct, object: StockExtended | ExtendedVendingTransaction) => {
        setIsLoading(true);
        try {
            if ('equipment' in object) {
                const eqId = object.equipment?.entity_id || object.equipment_id;
                if (eqId)
                    await EquipmentClient.resolveEquipments([eqId]).then((equipment) => {
                        updateWizardData({
                            stock: object,
                            product,
                            equipment: equipment.results[0]
                        });
                    });
                else
                    updateWizardData({
                        stock: object,
                        product
                    });
            } else {
                await localUpdateTransaction(object);
                updateWizardData({ product });
            }
            onSubmit();
        } finally {
            setIsLoading(false);
        }
    };

    const fetchData = async (queryParams?: ApiQueryParams<DefaultQueryParams>) => {
        const promises = [];
        if (allowedTypes.includes(Application.VENDING)) {
            promises.push(
                TransactionVendingClient.getReturns({
                    ...queryParams,
                    spot_id: navigationState.spot?.id,
                    state: TransactionStateName.TRANSACTION_PICK_UP_PENDING
                })
            );
        }
        if (allowedTypes.includes(Application.LENDING)) {
            promises.push(StockExtendedClient.fetchReusableStocksExtended({ ...queryParams, equipment_state: EquipmentStateName.EQUIPMENT_REVIEW_REQUIRED }));
        }

        return await Promise.all(promises).then((responses) => {
            const combinedResults: (StockExtended | ExtendedVendingTransaction)[] = [];
            let count = 0;
            responses.forEach((response) => {
                if ('results' in response) {
                    combinedResults.push(...response.results);
                }
                count += response.count;
            });
            return {
                count,
                results: combinedResults
            };
        });
    };

    return {
        isLoading,
        setIsLoading,
        fetchData,
        onClick
    };
};

export default useReviewReturns;
